import type { ComponentProps } from "react";

import { cn } from "../lib/cn";

/** Shop condition scale for raw singles (CLAUDE.md): A 美品 · B 軽い傷 · C 傷あり · D ダメージ. */
export const RAW_RANKS = ["A", "B", "C", "D"] as const;
export type RawRank = (typeof RAW_RANKS)[number];

/** i18n keys (namespace `common`) the apps translate for the rank description. */
export const RAW_RANK_LABEL_KEYS: Readonly<Record<RawRank, string>> = {
  A: "rank.A",
  B: "rank.B",
  C: "rank.C",
  D: "rank.D",
};

const RANK_CLASS: Readonly<Record<RawRank, string>> = {
  A: "bg-rank-a",
  B: "bg-rank-b",
  C: "bg-rank-c",
  D: "bg-rank-d",
};

export function isRawRank(value: unknown): value is RawRank {
  return typeof value === "string" && (RAW_RANKS as readonly string[]).includes(value);
}

export interface RankBadgeProps extends Omit<ComponentProps<"span">, "children"> {
  rank: RawRank;
  /** Translated description (e.g. "美品") — rendered next to the letter when provided. */
  label?: string;
  size?: "sm" | "md" | "lg";
}

/**
 * Rank badge for raw cards — must appear on product page, tile, POS and receipts. The letter is
 * always rendered (colour is never the only cue) and exposed to assistive tech via `aria-label`.
 */
export function RankBadge({ rank, label, size = "md", className, ...props }: RankBadgeProps) {
  const sizeClass =
    size === "sm"
      ? "h-5 min-w-5 text-xs"
      : size === "lg"
        ? "h-8 min-w-8 text-base"
        : "h-6 min-w-6 text-sm";
  return (
    <span
      data-slot="rank-badge"
      data-rank={rank}
      // `aria-label` is only allowed on elements with a role (axe aria-prohibited-attr): the badge
      // is announced as one image-like unit "B — 軽い傷".
      role="img"
      aria-label={label ? `${rank} — ${label}` : rank}
      className={cn("inline-flex items-center gap-1 font-bold text-rank-foreground", className)}
      {...props}
    >
      <span
        aria-hidden="true"
        className={cn(
          "inline-flex items-center justify-center rounded-sm px-1 leading-none",
          sizeClass,
          RANK_CLASS[rank],
        )}
      >
        {rank}
      </span>
      {label ? <span className="text-xs font-medium text-foreground">{label}</span> : null}
    </span>
  );
}
